<template>
  <div class="table-of-contents">
    <div class="toc-header" @click="toggleToc">
      <h3 class="toc-title">Table of Contents</h3>
      <button class="toc-toggle" aria-label="Toggle table of contents">
        <svg
          class="toc-toggle-icon"
          :class="{ rotated: isOpen }"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>

    <nav class="toc-nav" :class="{ 'is-open': isOpen }">
      <ul class="toc-list">
        <li
          v-for="item in tocItems"
          :key="item.id"
          class="toc-item"
          :class="{
            active: activeSection === item.id,
            [`level-${item.level}`]: true,
          }"
        >
          <a
            :href="`#${item.id}`"
            @click.prevent="scrollToSection(item.id)"
            class="toc-link"
          >
            {{ item.text }}
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
export default {
  name: "TableOfContents",
  props: {
    headings: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeSection: null,
      observer: null,
      isOpen: false, // New state for dropdown - starts closed
    };
  },
  computed: {
    tocItems() {
      return this.headings.map((heading) => ({
        id: heading.id,
        text: heading.text,
        level: heading.level,
      }));
    },
  },
  mounted() {
    // Add a small delay to ensure DOM elements are ready
    this.$nextTick(() => {
      setTimeout(() => {
        this.setupIntersectionObserver();
        this.setupScrollListener();
      }, 100);
    });
  },
  beforeUnmount() {
    try {
      if (this.observer && typeof this.observer.disconnect === "function") {
        this.observer.disconnect();
        this.observer = null;
      }
    } catch (disconnectError) {
      console.warn("Error disconnecting observer:", disconnectError);
    }

    try {
      window.removeEventListener("scroll", this.handleScroll);
    } catch (removeListenerError) {
      console.warn("Error removing scroll listener:", removeListenerError);
    }
  },
  methods: {
    setupIntersectionObserver() {
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                // Use intersection observer as backup method
                if (this.activeSection !== entry.target.id) {
                  this.activeSection = entry.target.id;
                  console.log(
                    "Intersection observer updated active section to:",
                    entry.target.id
                  );
                }
              }
            });
          },
          {
            rootMargin: "-20% 0px -70% 0px",
            threshold: 0,
          }
        );

        // Observe all heading elements
        this.headings.forEach((heading) => {
          try {
            const element = document.getElementById(heading.id);
            if (
              element &&
              element instanceof Element &&
              document.contains(element)
            ) {
              this.observer.observe(element);
              console.log("Successfully observing element:", heading.id);
            } else {
              console.warn(
                "Element not found or invalid for heading:",
                heading.id
              );
            }
          } catch (observeError) {
            console.warn(
              "Failed to observe element for heading:",
              heading.id,
              observeError
            );
          }
        });
      }
    },

    setupScrollListener() {
      window.addEventListener("scroll", this.handleScroll);
    },

    handleScroll() {
      // Check which section is currently active based on scroll position
      this.updateActiveSectionFromScroll();
    },

    updateActiveSectionFromScroll() {
      if (!this.headings || this.headings.length === 0) return;

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const offset = 150; // Account for fixed header and some buffer

      // Find which section is currently in view
      for (let i = this.headings.length - 1; i >= 0; i--) {
        const heading = this.headings[i];
        const element = document.getElementById(heading.id);
        if (element) {
          const elementTop = element.offsetTop;
          if (scrollTop + offset >= elementTop) {
            // Only update if it's different to avoid unnecessary re-renders
            if (this.activeSection !== heading.id) {
              this.activeSection = heading.id;
              console.log(
                "Active section updated to:",
                heading.text,
                heading.id
              );
            }
            break;
          }
        }
      }
    },

    toggleToc() {
      this.isOpen = !this.isOpen;
      // Emit event to parent component
      this.$emit("toc-toggle", this.isOpen);
    },

    scrollToSection(id) {
      const element = document.getElementById(id);
      if (element) {
        const offset = 120; // Account for fixed header and some buffer
        const elementPosition = element.offsetTop - offset;

        // Close TOC on mobile after clicking
        if (window.innerWidth <= 1024) {
          this.isOpen = false;
        }

        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });

        // Don't immediately update active section - let scroll detection handle it
        // This prevents flickering
      }
    },
  },
};
</script>

<style scoped>
/* Ensure sticky positioning works */
.table-of-contents {
  position: fixed;
  top: 5rem; /* Account for fixed back link header (4rem) + some spacing (1rem) */
  right: 2rem;
  width: 320px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: calc(100vh - 7rem); /* Adjust for fixed header */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: right 0.3s ease, width 0.3s ease; /* Smooth transitions for responsive changes */
}

/* Force sticky behavior */
.table-of-contents.is-sticky {
  position: fixed !important;
  top: 2rem !important;
  right: 2rem !important;
  z-index: 100 !important;
}

.toc-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.toc-header:hover {
  background-color: #f9fafb;
}

.toc-title {
  font-family: "Outfit", sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  user-select: none;
}

.toc-toggle {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toc-toggle:hover {
  background-color: #e5e7eb;
}

.toc-toggle-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
  color: #6b7280;
}

.toc-toggle-icon.rotated {
  transform: rotate(180deg);
}

.toc-nav {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 8rem);
  padding: 0.5rem 0;
}

.toc-nav.is-open {
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
}

/* Custom scrollbar styling */
.toc-nav::-webkit-scrollbar {
  width: 6px;
}

.toc-nav::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.toc-nav::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.toc-nav::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Firefox scrollbar */
.toc-nav {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.toc-nav:not(.is-open) {
  max-height: 0;
  overflow: hidden;
  padding: 0;
}

/* Ensure TOC items don't get cut off */
.toc-item:last-child {
  margin-bottom: 0;
}

.toc-item:last-child .toc-link {
  margin-bottom: 0;
}

/* Smooth content fade */
.toc-list {
  transition: opacity 0.3s ease;
}

.toc-nav:not(.is-open) .toc-list {
  opacity: 0;
}

.toc-nav.is-open .toc-list {
  opacity: 1;
}

.toc-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.toc-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.2s ease, border-left-color 0.2s ease;
  cursor: pointer;
  border-left: 3px solid transparent; /* Always have left border, transparent by default */
}

.toc-item:hover {
  background-color: #f3f4f6;
}

.toc-item.active {
  background-color: #f0fdf4; /* Light green background */
  border-left: 3px solid #22c55e; /* Green left border */
}

.toc-item.active .toc-link {
  color: #15803d; /* Darker green text */
  font-weight: 600;
}

.toc-link {
  display: block;
  text-decoration: none;
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.4;
  transition: color 0.2s ease;
}

.toc-item:hover .toc-link {
  color: #1f2937;
}

.toc-item.level-1 {
  margin-left: 0;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.toc-item.level-2 {
  margin-left: 1rem;
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.toc-item.level-3 {
  margin-left: 2rem;
  font-weight: 400;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

/* Add subtle separators between major sections */
.toc-item.level-1:not(:first-child) {
  border-top: 1px solid #f3f4f6;
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

/* Enhanced visual hierarchy with subtle indicators */
.toc-item.level-1 .toc-link::before {
  content: "📋";
  margin-right: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.7;
}

/* Enhanced level-specific styling */
.toc-item.level-1 .toc-link {
  color: #1f2937;
  font-weight: 600;
}

.toc-item.level-1.active .toc-link {
  color: #1d4ed8;
  font-weight: 700;
}

.toc-item.level-2 .toc-link {
  color: #374151;
  font-weight: 500;
}

.toc-item.level-3 .toc-link {
  color: #4b5563;
  font-weight: 400;
}

/* Mobile styles - hide on mobile */
@media (max-width: 1024px) {
  .table-of-contents {
    display: none;
  }
}

/* Ensure TOC doesn't overlap content on smaller screens */
@media (max-width: 1400px) {
  .table-of-contents {
    right: 1rem;
  }
}

/* Smooth transitions */
.toc-link {
  transition: all 0.2s ease;
}

/* Hover effects */
.toc-item:hover .toc-link {
  transform: translateX(2px);
}

/* Active state animations */
.toc-item.active .toc-link {
  transform: translateX(4px);
}

/* Responsive adjustments - maintain consistent width */
@media (max-width: 1509px) {
  .table-of-contents {
    right: 1rem;
    width: 320px; /* Keep same width as desktop */
  }
}

@media (max-width: 1200px) {
  .table-of-contents {
    right: 0.5rem;
    width: 320px; /* Keep same width as desktop */
  }

  .toc-nav.is-open {
    max-height: calc(100vh - 9rem); /* Adjust for fixed header */
  }
}

@media (max-width: 1024px) {
  .table-of-contents {
    display: none; /* Hide on mobile */
  }
}
</style>
